/**
 * Variable slice (SCRUM-1952, RTK bundle 3) — the former switch reducer
 * variableReducer.js as createSlice. Behaviour-preserving: every case answers
 * the same legacy action type string (types.js) that variableActions.js already
 * dispatches, and returns the same state. The actions module is unchanged, so
 * no importer changes. `initialState` stays a named export: the pagination test
 * builds its store from it.
 * Characterised by tests/integration/reducers/contentReducers.characterisation.test.js.
 */
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  ADD_VARIABLE,
  DELETE_VARIABLE,
  GET_VARIABLES,
  UPDATE_VARIABLE,
  ADD_ERROR,
  UPDATE_ERROR,
  GET_ERROR,
  DELETE_ERROR,
} from "./types";

/** A variable definition as GET /api/variables/list_pagination returns it. */
type VariableRecord = { _id?: string } & Record<string, unknown>;

/** One error slot: the server's error body, or `{}` when there is none. */
type ErrorSlot = Record<string, unknown>;

type ErrorSlotName = "add" | "get" | "update" | "delete";

type VariableState = {
  variables: VariableRecord[];
  errors: Record<ErrorSlotName, ErrorSlot>;
  added: boolean;
  updated: boolean;
  dataSize: number;
};

/** GET_VARIABLES' payload: one page of the list plus the total. */
type VariablesPage = { variables?: VariableRecord[]; configuration: { size: number } };

export const initialState: VariableState = {
  variables: [],
  errors: { add: {}, delete: {}, update: {}, get: {} },
  added: false,
  updated: false,
  dataSize: 0,
};

/** A fresh, empty error set; a successful write clears every slot. */
const noErrors = (): VariableState["errors"] => ({ add: {}, delete: {}, update: {}, get: {} });

/** The case reducer that stores `action.payload` in one error slot. */
const setError = <T extends string>(slot: ErrorSlotName) =>
  (state: VariableState, action: PayloadAction<ErrorSlot, T>) => ({ ...state, errors: { ...state.errors, [slot]: action.payload } });

const variableSlice = createSlice({
  name: "variables",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(ADD_VARIABLE, (state, action: PayloadAction<VariableRecord, typeof ADD_VARIABLE>) => ({
        ...state,
        variables: [action.payload, ...state.variables],
        added: true,
        errors: noErrors(),
      }))
      .addCase(DELETE_VARIABLE, (state, action: PayloadAction<string, typeof DELETE_VARIABLE>) => ({
        ...state,
        variables: state.variables.filter((variable) => variable._id !== action.payload),
      }))
      .addCase(UPDATE_VARIABLE, (state, action: PayloadAction<VariableRecord, typeof UPDATE_VARIABLE>) => ({
        ...state,
        variables: [action.payload, ...state.variables.filter((variable) => variable._id !== action.payload._id)],
        updated: true,
        errors: noErrors(),
      }))
      .addCase(GET_VARIABLES, (state, action: PayloadAction<VariablesPage, typeof GET_VARIABLES>) => {
        // Paginated append (getVariables fetches the first page, then the rest), but
        // MERGE by _id so a repeated fetch of a page already in state does not
        // duplicate rows — e.g. a re-mount, or React StrictMode's dev double-invoke.
        // Load-bearing since SCRUM-1112 dropped savedSkip: a second mount now
        // re-requests the first page instead of skipping past it. Drop any existing
        // entry the incoming page also carries, then append the incoming page;
        // later pages still accumulate because they carry new _ids.
        const incoming = action.payload.variables || [];
        const incomingIds = new Set(incoming.map((v) => v._id));
        return {
          ...state,
          variables: [...state.variables.filter((v) => !incomingIds.has(v._id)), ...incoming],
          dataSize: action.payload.configuration.size,
        };
      })
      .addCase(ADD_ERROR, setError<typeof ADD_ERROR>("add"))
      .addCase(GET_ERROR, setError<typeof GET_ERROR>("get"))
      .addCase(UPDATE_ERROR, setError<typeof UPDATE_ERROR>("update"))
      .addCase(DELETE_ERROR, setError<typeof DELETE_ERROR>("delete"));
  },
});

export default variableSlice.reducer;
